import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import api from '../services/api';

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

interface SellerProfile {
  seller_id: string;
  business_name: string;
  contact_phone: string | null;
  city: string | null;
  is_verified: boolean;
  verification_status: string;
  rating: number;
  total_sales: number;
  active_listings: number;
  created_at: string;
}

interface SellerStats {
  seller_id: string;
  rating: number;
  rating_count: number;
  total_revenue: number;
  total_sales: number;
  verification_status: string;
}

interface ChartDataPoint {
  label: string;
  amount: number;
}

interface RevenueData {
  period: string;
  total: number;
  order_count: number;
  avg_order_value: number;
  best_seller: string;
  chart_data: ChartDataPoint[];
  transactions: { title: string; date: string; amount: number }[];
}

interface IncomingOrder {
  order_id: string;
  listing_title: string;
  price_lkr: number;
  payment_method: string;
  status: string;
  buyer_name: string;
  buyer_phone: string | null;
  created_at: string;
}

interface MyListing {
  id: string;
  title: string;
  condition: string;
  price_lkr: number;
  city: string | null;
  status: string;
  photos: string[];
  stock_quantity: number;
  created_at: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER: safe API fetch (returns null on 404 / error)
// ═══════════════════════════════════════════════════════════════════════════════

async function safeFetch<T>(url: string): Promise<T | null> {
  try {
    const res = await api.get(url);
    return (res.data ?? res) as T;
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SKELETON COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm p-6 animate-pulse ${className}`}>
    <div className="w-10 h-10 rounded-lg bg-cream-200 mb-4" />
    <div className="h-8 w-24 bg-cream-200 rounded mb-2" />
    <div className="h-4 w-16 bg-cream-200 rounded" />
  </div>
);

const SkeletonChart: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
    <div className="h-6 w-40 bg-cream-200 rounded mb-4" />
    <div className="h-64 bg-cream-200 rounded" />
  </div>
);

const SkeletonTable: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
    <div className="h-6 w-40 bg-cream-200 rounded mb-4" />
    {[...Array(3)].map((_, i) => (
      <div key={i} className="flex gap-4 mb-3">
        <div className="h-5 flex-1 bg-cream-200 rounded" />
        <div className="h-5 w-20 bg-cream-200 rounded" />
        <div className="h-5 w-16 bg-cream-200 rounded" />
      </div>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [profile, setProfile] = useState<SellerProfile | null>(null);
  const [stats, setStats] = useState<SellerStats | null>(null);
  const [revenue, setRevenue] = useState<RevenueData | null>(null);
  const [revenuePeriod, setRevenuePeriod] = useState<'week' | 'month' | 'year'>('week');
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [orders, setOrders] = useState<IncomingOrder[]>([]);
  const [listings, setListings] = useState<MyListing[]>([]);

  // Broadcast modal
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // ── Fetch All Data ───────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, statsRes, revenueRes, ordersRes, listingsRes] =
        await Promise.all([
          safeFetch<SellerProfile>('/marketplace/sellers/me'),
          safeFetch<SellerStats>('/marketplace/seller/stats'),
          safeFetch<RevenueData>(`/marketplace/seller/revenue?period=${revenuePeriod}`),
          safeFetch<IncomingOrder[]>('/marketplace/orders/incoming'),
          safeFetch<MyListing[]>('/marketplace/listings/my'),
        ]);

      setProfile(profileRes);
      setStats(statsRes);
      setRevenue(revenueRes);
      setOrders(Array.isArray(ordersRes) ? ordersRes : []);
      setListings(Array.isArray(listingsRes) ? listingsRes : []);
    } catch {
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Revenue period toggle ────────────────────────────────────────────────
  const fetchRevenue = useCallback(async (period: 'week' | 'month' | 'year') => {
    setRevenuePeriod(period);
    setRevenueLoading(true);
    const res = await safeFetch<RevenueData>(`/marketplace/seller/revenue?period=${period}`);
    setRevenue(res);
    setRevenueLoading(false);
  }, []);

  // ── Broadcast ────────────────────────────────────────────────────────────
  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) return;
    setBroadcastSending(true);
    try {
      await api.post('/marketplace/sellers/broadcast', { message: broadcastMsg });
      setToastMessage('Announcement sent to all followers!');
    } catch {
      setToastMessage('🚧 Broadcast coming soon — feature under development.');
    } finally {
      setBroadcastSending(false);
      setShowBroadcast(false);
      setBroadcastMsg('');
      setTimeout(() => setToastMessage(null), 3500);
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────
  const pendingOrders = orders.filter((o) => o.status === 'pending');
  const lowStockListings = listings.filter(
    (l) => l.stock_quantity <= 1 && l.status === 'active'
  );
  const recentOrders = orders.slice(0, 5);

  // ── Format helpers ───────────────────────────────────────────────────────
  const formatCurrency = (n: number) =>
    `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 0 })}`;

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const statusColor: Record<string, string> = {
    pending: 'bg-amber-50 text-amber border border-amber-100',
    confirmed: 'bg-blue-50 text-blue-600 border border-blue-100',
    shipped: 'bg-purple-50 text-purple-600 border border-purple-100',
    delivered: 'bg-emerald-50 text-emerald border border-emerald-100',
    cancelled: 'bg-coral-50 text-coral border border-coral-100',
    paid: 'bg-blue-50 text-blue-600 border border-blue-100',
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ERROR STATE
  // ═════════════════════════════════════════════════════════════════════════
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-coral-50 border border-coral-200 rounded-xl p-6 flex flex-col items-center gap-4 text-center">
          <span className="text-3xl">⚠️</span>
          <p className="text-coral-700 font-medium">{error}</p>
          <button
            onClick={fetchAll}
            className="px-5 py-2 bg-coral text-white rounded-lg font-semibold text-sm
                       hover:bg-coral-700 transition-colors shadow-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-fade-in">
        {/* Skeleton cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <SkeletonChart />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <SkeletonTable />
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* ─── GREETING ───────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-bold text-teal-900">
          Welcome back{profile?.business_name ? `, ${profile.business_name}` : ''} 👋
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Here's what's happening with your store today.
        </p>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           SECTION 1 — TOP STATS ROW (4 cards)
           ═══════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" id="stats-row">
        {/* Orders (total_sales from seller profile) */}
        <StatCard
          icon="📦"
          label="Total Orders"
          value={stats?.total_sales?.toString() ?? profile?.total_sales?.toString() ?? '—'}
          trend={null}
          accentColor="bg-teal-50"
        />
        {/* Revenue */}
        <StatCard
          icon="💰"
          label="Total Revenue"
          value={
            stats?.total_revenue != null
              ? formatCurrency(stats.total_revenue)
              : '—'
          }
          trend={null}
          accentColor="bg-emerald-50"
        />
        {/* Active Listings */}
        <StatCard
          icon="📚"
          label="Active Listings"
          value={profile?.active_listings?.toString() ?? '—'}
          trend={null}
          accentColor="bg-amber-50"
        />
        {/* Rating */}
        <StatCard
          icon="⭐"
          label="Seller Rating"
          value={
            stats?.rating != null && stats.rating > 0
              ? `${stats.rating.toFixed(1)}`
              : profile?.rating != null && profile.rating > 0
              ? `${profile.rating.toFixed(1)}`
              : '—'
          }
          subValue={
            stats?.rating_count
              ? `${stats.rating_count} review${stats.rating_count !== 1 ? 's' : ''}`
              : undefined
          }
          trend={null}
          accentColor="bg-mint"
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           SECTION 2 — REVENUE CHART (Recharts)
           ═══════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm p-6" id="revenue-chart">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <h2 className="text-lg font-bold text-teal-900">Revenue Overview</h2>
          <div className="flex gap-1 bg-cream rounded-lg p-1">
            {(['week', 'month', 'year'] as const).map((p) => (
              <button
                key={p}
                onClick={() => fetchRevenue(p)}
                className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all duration-200
                  ${
                    revenuePeriod === p
                      ? 'bg-teal text-white shadow-sm'
                      : 'text-gray-500 hover:text-teal-700 hover:bg-cream-200'
                  }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {revenueLoading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-8 h-8 border-3 border-teal/20 border-t-teal rounded-full animate-spin" />
          </div>
        ) : revenue?.chart_data && revenue.chart_data.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue.chart_data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1D9E75" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#E1F5EE" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e5db" vertical={false} />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  tickFormatter={(v: any) => { const n = Number(v); return n >= 1000 ? `${(n / 1000).toFixed(0)}k` : n.toString(); }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e8e5db',
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    fontSize: '13px',
                  }}
                  formatter={(value: any) => [formatCurrency(Number(value ?? 0)), 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#1D9E75"
                  strokeWidth={2.5}
                  fill="url(#revenueGradient)"
                  dot={{ r: 4, fill: '#1D9E75', strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 6, fill: '#1D9E75', strokeWidth: 2, stroke: '#fff' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex flex-col items-center justify-center text-gray-400">
            <span className="text-4xl mb-2">📈</span>
            <p className="text-sm">No revenue data for this period yet.</p>
          </div>
        )}

        {/* Quick revenue stats below chart */}
        {revenue && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-cream-200">
            <MiniStat label="Period Total" value={formatCurrency(revenue.total)} />
            <MiniStat label="Orders" value={revenue.order_count.toString()} />
            <MiniStat label="Avg. Order" value={formatCurrency(revenue.avg_order_value)} />
            <MiniStat label="Best Seller" value={revenue.best_seller ?? '—'} />
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           SECTION 3 — PERFORMANCE METRICS ROW
           ═══════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" id="performance-metrics">
        <MetricCard
          icon="⚡"
          label="Avg Response Time"
          value="< 2 hrs"
          sublabel="Great response speed"
          color="text-emerald"
        />
        <MetricCard
          icon="✅"
          label="Completion Rate"
          value={
            stats?.total_sales && stats.total_sales > 0
              ? `${Math.min(100, Math.round((stats.total_sales / (stats.total_sales + pendingOrders.length)) * 100))}%`
              : '—'
          }
          sublabel="Order fulfillment"
          color="text-teal"
        />
        <MetricCard
          icon="⭐"
          label="This Month Reviews"
          value={stats?.rating_count?.toString() ?? '—'}
          sublabel="Total ratings received"
          color="text-amber"
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           SECTION 4 — PENDING ACTIONS BANNER
           ═══════════════════════════════════════════════════════════════════ */}
      {pendingOrders.length > 0 && (
        <div
          className="bg-amber-50 border border-amber-100 rounded-xl p-5 flex flex-col sm:flex-row
                     items-center justify-between gap-3 animate-slide-up"
          id="pending-actions"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <p className="text-amber-800 font-semibold text-sm">
              {pendingOrders.length} order{pendingOrders.length !== 1 ? 's' : ''} waiting for
              confirmation
            </p>
          </div>
          <button
            onClick={() => navigate('/orders')}
            className="px-5 py-2 bg-amber text-white rounded-lg font-semibold text-sm
                       hover:bg-amber-700 transition-colors whitespace-nowrap shadow-sm"
          >
            Go to Orders →
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
           SECTION 5 — LOW STOCK ALERT
           ═══════════════════════════════════════════════════════════════════ */}
      {lowStockListings.length > 0 && (
        <div
          className="bg-white rounded-xl shadow-sm border-l-4 border-coral p-5 animate-slide-up"
          id="low-stock-alert"
        >
          <h3 className="text-sm font-bold text-teal-900 flex items-center gap-2 mb-3">
            <span className="text-lg">📚</span> Low Stock Alert
          </h3>
          <div className="space-y-2">
            {lowStockListings.map((l) => (
              <div
                key={l.id}
                className="flex items-center justify-between py-2 px-3 bg-coral-50 rounded-lg"
              >
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">"{l.title}"</span>
                  <span className="text-coral ml-2 font-medium">
                    {l.stock_quantity === 0 ? '— Out of stock!' : `— Only ${l.stock_quantity} copy left`}
                  </span>
                </p>
                <button
                  onClick={() => navigate('/catalogue')}
                  className="text-xs font-semibold text-coral hover:text-coral-700
                             transition-colors px-3 py-1 rounded-md hover:bg-coral-100"
                >
                  Edit
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
           SECTION 6 — RECENT ORDERS TABLE
           ═══════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm p-6" id="recent-orders">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-teal-900">Recent Orders</h2>
          <button
            onClick={() => navigate('/orders')}
            className="text-xs font-semibold text-emerald hover:text-emerald-700
                       transition-colors"
          >
            View All Orders →
          </button>
        </div>

        {recentOrders.length > 0 ? (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-cream-200">
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-3 pr-4">
                    Order ID
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-3 pr-4">
                    Book Title
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-3 pr-4">
                    Buyer
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-3 pr-4">
                    Price
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-3 pr-4">
                    Status
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-3">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o, idx) => (
                  <tr
                    key={o.order_id}
                    className={`hover:bg-cream-50 transition-colors ${
                      idx !== recentOrders.length - 1 ? 'border-b border-cream-100' : ''
                    }`}
                  >
                    <td className="py-3 pr-4 text-sm text-gray-500 font-mono">
                      #{o.order_id.slice(0, 8)}
                    </td>
                    <td className="py-3 pr-4 text-sm font-semibold text-teal-900">
                      {o.listing_title}
                    </td>
                    <td className="py-3 pr-4 text-sm text-gray-600">{o.buyer_name}</td>
                    <td className="py-3 pr-4 text-sm font-semibold text-teal-900">
                      {formatCurrency(o.price_lkr)}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize
                          ${statusColor[o.status] ?? 'bg-gray-100 text-gray-500'}`}
                      >
                        {o.status}
                      </span>
                    </td>
                    <td className="py-3 text-sm text-gray-500">{formatDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 text-gray-400">
            <span className="text-3xl block mb-2">📋</span>
            <p className="text-sm">No orders received yet. They'll show up here!</p>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           SECTION 7 — RECENT REVIEWS PREVIEW
           ═══════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl shadow-sm p-6" id="recent-reviews">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-teal-900">Recent Reviews</h2>
          <button
            onClick={() => navigate('/reviews')}
            className="text-xs font-semibold text-emerald hover:text-emerald-700
                       transition-colors"
          >
            View All Reviews →
          </button>
        </div>

        {/* No review listing endpoint — show placeholder */}
        <div className="space-y-4">
          {stats?.rating_count && stats.rating_count > 0 ? (
            <div className="text-center py-8 text-gray-400">
              <span className="text-3xl block mb-2">⭐</span>
              <p className="text-sm">
                You have {stats.rating_count} review{stats.rating_count !== 1 ? 's' : ''}. 
                Visit the Reviews page to see details.
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <span className="text-3xl block mb-2">💬</span>
              <p className="text-sm">No reviews yet. Deliver great orders to earn reviews!</p>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           SECTION 8 — FOLLOWER BROADCAST BUTTON
           ═══════════════════════════════════════════════════════════════════ */}
      <div
        className="bg-white rounded-xl shadow-sm p-6 border border-cream-200"
        id="broadcast-section"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl mt-0.5">📢</span>
            <div>
              <h3 className="text-sm font-bold text-teal-900">Broadcast to Followers</h3>
              <p className="text-xs text-gray-500 mt-1">
                Send a message to all your followers about new books, offers, or updates.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowBroadcast(true)}
            className="px-5 py-2.5 bg-teal text-white rounded-lg font-semibold text-sm
                       hover:bg-teal-800 transition-all duration-200 whitespace-nowrap
                       shadow-lg shadow-teal/20 hover:shadow-teal/30"
          >
            Send Announcement →
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           BROADCAST MODAL
           ═══════════════════════════════════════════════════════════════════ */}
      {showBroadcast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setShowBroadcast(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-md animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-teal-900 flex items-center gap-2">
                <span>📢</span> Send Announcement
              </h3>
              <button
                onClick={() => setShowBroadcast(false)}
                className="w-8 h-8 rounded-full bg-cream hover:bg-cream-200 flex items-center
                           justify-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            <textarea
              value={broadcastMsg}
              onChange={(e) => setBroadcastMsg(e.target.value.slice(0, 200))}
              placeholder="Write your announcement..."
              className="w-full h-32 px-4 py-3 bg-cream rounded-xl border border-cream-300
                         text-sm text-teal-900 placeholder-gray-400 resize-none
                         focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal-300
                         transition-all"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-400">{broadcastMsg.length}/200</span>
              <button
                onClick={handleBroadcast}
                disabled={!broadcastMsg.trim() || broadcastSending}
                className="px-5 py-2.5 bg-teal text-white rounded-lg font-semibold text-sm
                           hover:bg-teal-800 transition-all disabled:opacity-50
                           disabled:cursor-not-allowed shadow-lg shadow-teal/20
                           flex items-center gap-2"
              >
                {broadcastSending && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                Send to All Followers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
           TOAST
           ═══════════════════════════════════════════════════════════════════ */}
      {toastMessage && (
        <div
          className="fixed bottom-6 right-6 z-50 bg-teal-900 text-white px-5 py-3 rounded-xl
                     shadow-xl text-sm font-medium animate-slide-up max-w-sm"
        >
          {toastMessage}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

interface StatCardProps {
  icon: string;
  label: string;
  value: string;
  subValue?: string;
  trend: { value: string; up: boolean } | null;
  accentColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, subValue, trend, accentColor }) => (
  <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow duration-300 group">
    <div className="flex items-start justify-between mb-3">
      <div
        className={`w-10 h-10 rounded-lg ${accentColor} flex items-center justify-center text-lg
                    group-hover:scale-110 transition-transform duration-300`}
      >
        {icon}
      </div>
      {trend && (
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            trend.up ? 'bg-emerald-50 text-emerald' : 'bg-coral-50 text-coral'
          }`}
        >
          {trend.up ? '↑' : '↓'} {trend.value}
        </span>
      )}
    </div>
    <p className="text-2xl lg:text-3xl font-bold text-teal-900 leading-tight">{value}</p>
    <p className="text-xs text-gray-400 font-medium mt-1">{label}</p>
    {subValue && <p className="text-xs text-gray-500 mt-0.5">{subValue}</p>}
  </div>
);

interface MetricCardProps {
  icon: string;
  label: string;
  value: string;
  sublabel: string;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ icon, label, value, sublabel, color }) => (
  <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow duration-300">
    <div className="flex items-center gap-3 mb-3">
      <span className="text-lg">{icon}</span>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-gray-400 mt-1">{sublabel}</p>
  </div>
);

interface MiniStatProps {
  label: string;
  value: string;
}

const MiniStat: React.FC<MiniStatProps> = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400 font-medium">{label}</p>
    <p className="text-sm font-bold text-teal-900 mt-0.5 truncate">{value}</p>
  </div>
);

export default Dashboard;
